/**
 * AirRiskAnswers Component
 *
 * Displays the five air risk answer fields for SORA evidence review.
 * Uses the EvidenceTable component to display extracted values with citations
 * and confidence scores.
 *
 * Air Risk Fields:
 * 1. Operating in Atypical Air Environment (AAE) only (yes/no)
 * 2. Operating in area of known instrument flight procedures (IFP) (yes/no)
 * 3. Operating in area with VFR corridor / low level helicopters (yes/no)
 * 4. Flying more than 500ft above ground level (yes/no)
 * 5. Type of operation (VLOS, BVLOS with visual mitigations, BVLOS)
 *
 * Requirements: 6.1, 6.2
 */

import React, { useMemo } from 'react';
import { EvidenceTable, EvidenceItem, EvidenceColumn } from '../EvidenceTable';
import { AirRiskAnswer, AirRiskField, Citation } from '../../types/extraction';
import styles from './AirRiskAnswers.module.css';

/**
 * Human-readable labels for air risk fields
 */
const AIR_RISK_FIELD_LABELS: Record<AirRiskField, string> = {
  aae_only: 'Operating in Atypical Air Environment (AAE) only',
  known_ifp_area: 'Operating in area of known instrument flight procedures (IFP)',
  vfr_corridor_area: 'Operating in area with VFR corridor / low level helicopters',
  above_500ft_agl: 'Flying more than 500ft above ground level',
  operation_type: 'Type of operation',
};

/**
 * Order of fields for display
 */
const FIELD_ORDER: AirRiskField[] = [
  'aae_only',
  'known_ifp_area',
  'vfr_corridor_area',
  'above_500ft_agl',
  'operation_type',
];

/**
 * Column configuration for the air risk answers table
 */
const AIR_RISK_COLUMNS: EvidenceColumn[] = [
  { key: 'field', label: 'Field', width: '30%' },
  { key: 'value', label: 'Extracted Value', width: '25%' },
  { key: 'confidence', label: 'Confidence', width: '15%' },
  { key: 'citations', label: 'Citations', width: '20%' },
  { key: 'feedback', label: 'Feedback', width: '10%' },
];

/**
 * Props for the AirRiskAnswers component
 */
export interface AirRiskAnswersProps {
  /** Array of air risk answers to display */
  answers: AirRiskAnswer[];
  /** Callback when a citation is clicked */
  onCitationClick: (citation: Citation) => void;
  /** Callback when the feedback button is clicked for an answer */
  onFeedbackClick: (answerId: string) => void;
  /** Optional loading state */
  isLoading?: boolean;
  /** Optional title for the section */
  title?: string;
  /** Optional description for the section */
  description?: string;
}

/**
 * Formats a boolean or string value for display
 */
function formatDisplayValue(value: string | boolean): string {
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  // Handle operation type values
  switch (value) {
    case 'VLOS':
      return 'VLOS';
    case 'BVLOS_visual_mitigations':
      return 'BVLOS with visual mitigations';
    case 'BVLOS':
      return 'BVLOS';
    default:
      return value;
  }
}

/**
 * Converts an AirRiskAnswer to an EvidenceItem for the EvidenceTable
 */
function mapAnswerToEvidenceItem(answer: AirRiskAnswer): EvidenceItem {
  return {
    id: answer.id,
    field: AIR_RISK_FIELD_LABELS[answer.field],
    extractedValue: formatDisplayValue(answer.extractedValue),
    confidence: answer.confidence,
    citations: answer.citations,
    userCorrectedValue: answer.userCorrectedValue !== undefined
      ? formatDisplayValue(answer.userCorrectedValue)
      : undefined,
  };
}

/**
 * Sorts answers according to the defined field order
 */
function sortAnswersByFieldOrder(answers: AirRiskAnswer[]): AirRiskAnswer[] {
  return [...answers].sort((a, b) => {
    const indexA = FIELD_ORDER.indexOf(a.field);
    const indexB = FIELD_ORDER.indexOf(b.field);
    return indexA - indexB;
  });
}

/**
 * AirRiskAnswers displays the five air risk answer fields in a table format.
 * It uses the EvidenceTable component to provide consistent styling and functionality
 * including confidence badges, clickable citations, and feedback buttons.
 *
 * @example
 * <AirRiskAnswers
 *   answers={extractionResults.airRiskAnswers}
 *   onCitationClick={handleCitationClick}
 *   onFeedbackClick={handleFeedbackClick}
 * />
 */
export const AirRiskAnswers: React.FC<AirRiskAnswersProps> = ({
  answers,
  onCitationClick,
  onFeedbackClick,
  isLoading = false,
  title = 'Air Risk Answers',
  description = 'Review the extracted air risk information for your SORA application.',
}) => {
  // Sort answers by field order and convert to EvidenceItems
  const evidenceItems = useMemo(() => {
    const sortedAnswers = sortAnswersByFieldOrder(answers);
    return sortedAnswers.map(mapAnswerToEvidenceItem);
  }, [answers]);

  return (
    <section className={styles.container} aria-labelledby="air-risk-answers-title">
      <header className={styles.header}>
        <h2 id="air-risk-answers-title" className={styles.title}>
          {title}
        </h2>
        {description && (
          <p className={styles.description}>{description}</p>
        )}
      </header>

      <div className={styles.tableWrapper}>
        <EvidenceTable
          items={evidenceItems}
          columns={AIR_RISK_COLUMNS}
          onCitationClick={onCitationClick}
          onFeedbackClick={onFeedbackClick}
          isLoading={isLoading}
          emptyMessage="No air risk answers available. Please process your documents to extract evidence."
          ariaLabel="Air risk answers table"
        />
      </div>

      {!isLoading && answers.length > 0 && (
        <footer className={styles.footer}>
          <p className={styles.footerText}>
            <span className={styles.fieldCount}>{answers.length}</span> of{' '}
            <span className={styles.totalFields}>{FIELD_ORDER.length}</span> air risk fields extracted
          </p>
        </footer>
      )}
    </section>
  );
};

export default AirRiskAnswers;
